import { useEffect, useRef } from 'react';
import { useMatch } from 'react-router';
import { Search, X } from 'lucide-react';
import { isBlocked } from '../ui/keys';
import { useSearchStore, type Scope } from './searchStore';
import { useResults } from './useResults';
import { SearchResults } from './SearchResults';
import './Search.css';

/**
 * One input, two scopes, `Cmd/Ctrl + K`.
 *
 * Within a board it filters the tiles behind it — which is why this floats
 * rather than sitting over a backdrop, and why there's nothing to see here
 * when the scope is the room you're already looking at.
 */
export function SearchOverlay() {
  const open = useSearchStore((state) => state.open);
  const query = useSearchStore((state) => state.query);
  const scope = useSearchStore((state) => state.scope);
  const focus = useSearchStore((state) => state.focus);
  const setQuery = useSearchStore((state) => state.setQuery);
  const setScope = useSearchStore((state) => state.setScope);
  const close = useSearchStore((state) => state.close);

  const field = useRef<HTMLInputElement>(null);
  const onBoard = useMatch('/room/:roomId/*') !== null;

  // Scoping to "this room" off a board means scoping to nothing.
  useEffect(() => {
    if (open && !onBoard && scope === 'room') setScope('everywhere');
  }, [open, onBoard, scope, setScope]);

  // Selected, not just focused: a second Cmd+K almost always means "search for
  // something else", and typing should replace what's there.
  useEffect(() => {
    if (!open) return;
    field.current?.focus();
    field.current?.select();
  }, [open, focus]);

  // On the document, not only on the field: focus may have moved to a scope
  // button or a result by the time Escape is pressed, and it should still
  // dismiss.
  //
  // In the capture phase, because this listener is added when the bar opens
  // and every other Escape handler in the app was already listening. The thing
  // on top gets first refusal, and one press closes the search rather than
  // dropping the selection behind it.
  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || isBlocked() || event.key !== 'Escape') return;
      event.preventDefault();
      close();
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, close]);

  const results = useResults(scope === 'everywhere' ? query : '');

  if (!open) return null;

  return (
    <div className="search" role="search">
      <div className="search__dock">
        <div className="search__bar">
          <Search size={18} aria-hidden className="search__icon" />

          <input
            ref={field}
            type="search"
            className="search__field"
            value={query}
            placeholder={scope === 'room' ? 'Filter this room' : 'Search everything'}
            aria-label="Search"
            onChange={(event) => setQuery(event.target.value)}
          />

          <div className="search__scopes" role="group" aria-label="Where to search">
            <ScopeButton
              scope="room"
              selected={scope}
              disabled={!onBoard}
              onSelect={setScope}
              label="This Room"
            />
            <ScopeButton
              scope="everywhere"
              selected={scope}
              disabled={false}
              onSelect={setScope}
              label="Everything"
            />
          </div>

          <button type="button" className="search__close" aria-label="Close search" onClick={close}>
            <X size={16} aria-hidden />
          </button>
        </div>

        {/* Out of flow, so the board underneath keeps its own layout — only
            the bar takes a strip of the page. */}
        {scope === 'everywhere' && (
          <div className="search__panel">
            <SearchResults results={results} query={query} onPick={close} />
          </div>
        )}
      </div>
    </div>
  );
}

function ScopeButton({
  scope,
  selected,
  disabled,
  onSelect,
  label,
}: {
  scope: Scope;
  selected: Scope;
  disabled: boolean;
  onSelect: (scope: Scope) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      className={`search__scope${selected === scope ? ' search__scope--on' : ''}`}
      aria-pressed={selected === scope}
      disabled={disabled}
      onClick={() => onSelect(scope)}
    >
      {label}
    </button>
  );
}
